<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->


<template>
<div id="app" class="wrapper">
  <notifications position="top center" width="30%" :max="5">
    <template #body="props">
      <div class="notification vue-notification" :class="props.item.type">
        <button class="delete" @click="props.close"></button>
        <strong class="notification-title">
          {{props.item.title}}
        </strong>
        <div class="notification-content" v-html="props.item.text"></div>
      </div>
    </template>
  </notifications>

  <template v-if="!loading">
    <div class="box error" v-if="permissionError">
      <h2>Authntication Error</h2>
      You do not have permission to view this page.
    </div>

    <div class="box error" v-else-if="communicationError">
      <h2>
        {{$t('communication-error')}}
      </h2>
      {{$t('core-cannot-be-reached')}}
    </div>

    <login v-else-if="!currentUser" />

    <template v-else>
      <cytomine-navbar />
      <div class="bottom">
        <keep-alive include="cytomine-storage">
          <router-view v-if="currentUser" />
        </keep-alive>
      </div>
    </template>
  </template>
</div>
</template>

<script>
import axios from 'axios';
import {get} from '@/utils/store-helpers';
import {changeLanguageMixin} from '@/lang.js';

import CytomineNavbar from './components/navbar/CytomineNavbar.vue';
import Login from './components/user/Login.vue';

import {Cytomine} from 'cytomine-client';

import constants from '@/utils/constants.js';
import ifvisible from 'ifvisible';
ifvisible.setIdleDuration(constants.IDLE_DURATION);

import Token from '@/utils/token';

export default {
  name: 'app',
  components: {CytomineNavbar, Login},
  mixins: [changeLanguageMixin],
  data() {
    return {
      communicationError: false,
      loading: true,
      timeout: null,

      permissionError: false,
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    project: get('currentProject/project')
  },
  methods: {
    async loginWithToken() {
      try {
        let {shortTermToken} = await Cytomine.instance.loginWithToken(this.$route.query.username, this.$route.query.token);
        this.$store.commit('currentUser/setShortTermToken', shortTermToken);

        await this.fetchUser();
      }
      catch(error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('invalid-token')});
      }
    },
    async ping() {
      this.permissionError = !(await Token.isTokenValid());

      if(!ifvisible.now()){
        return; // window not visible or inactive user => stop pinging
      }
      try {
        let {authenticated, shortTermToken} = await Cytomine.instance.ping(this.project ? this.project.id : null);

        this.$store.commit('currentUser/setShortTermToken', shortTermToken);

        if(this.currentUser && !authenticated) {
          await this.$store.dispatch('logout');
        }
        if(!this.currentUser && authenticated) {
          await this.fetchUser();
        }
        this.communicationError = false;
      }
      catch(error) {
        console.log(error);
        if (error.toString().indexOf('401')!==-1) {
          this.communicationError = false;
          Cytomine.instance.logout();
        }
        else {
          this.communicationError = true;
        }
      }

      clearTimeout(this.timeout);
      this.timeout = setTimeout(this.ping, constants.PING_INTERVAL);
    },
    async fetchUser() {
      await this.$store.dispatch('currentUser/fetchUser');
      if(this.currentUser) {
        this.changeLanguage(this.currentUser.language);
      }
    }
  },
  async created() {
    let settings;
    await axios
      .get('configuration.json')
      .then(response => (settings = response.data));

    for (let i in settings) {
      if (Object.prototype.hasOwnProperty.call(constants, i)
        || i.includes('_NAMESPACE') || i.includes('_VERSION') || i.includes('_ENABLED')) {
        constants[i] = settings[i];
      }
    }
    Object.freeze(constants);

    new Cytomine(window.location.origin);

    if(this.$route.query.token && this.$route.query.username) {
      await this.loginWithToken();
    }
    await this.ping();
    this.loading = false;
    ifvisible.on('wakeup', this.ping);
  }
};
</script>

<style lang="scss">
@import '@/assets/styles/main.scss';

@font-face {
  font-family: 'cytomine';
  src: url('assets/cytomine-font.woff') format('woff');
}

html, body {
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

body {
  font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif, sans-serif;
  color: #333;
  background: #d4d4d4;
}

.wrapper {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  background: #d4d4d4;
}

.box.error {
  max-width: 600px;
  margin: auto;
  margin-top: 3rem;
}

.notifications {
  margin-top: 1em;
}

.notification.info {
  background: #77b1ea;
}

.bottom {
  flex: 1;
  overflow-y: auto;
  /* position: relative; */
}

h1 {
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  font-size: 1.25rem;
  text-align: center;
  padding: 0.9rem;
}

h2 {
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08rem;
  margin-bottom: 1rem;
}

.in-project {
  color: grey;
  font-size: 0.8em;
}

.input[readonly] {
  background-color: whitesmoke;
}

strong, .label {
  font-weight: 600 !important;
}

.content-wrapper {
  padding: 1.5% 2.5%;
  position: relative;
  min-height: 100%;
}

/* Filters */

.filters {
  background: #f8f8f8;
  margin-top: 1.2rem;
  border-radius: 10px;
  padding: 1rem;
}

.filter-label {
  text-transform: uppercase;
  font-size: 0.8em;
  margin-bottom: 0.5em;
  margin-left: 1em;
}

.filter-label .no-uppercase {
  text-transform: none;
}

.nb-active-filters {
  display: inline-block;
  background: $primary;
  color: $primary-invert;
  min-width: 1.25rem;
  height: 1.25rem;
  font-weight: 600;
  border-radius: 0.625rem;
  margin-left: 0.5em;
  font-size: 0.9em;
  line-height: 1.25em;
  padding: 0 0.25em;
  position: absolute;
  top: -0.3em;
  right: -0.6em;
}

$dark: #333;
$darker: #222;

.is-link {
  background-color: $primary !important;
}

.opaque-bg {
  background-color: #555;
}

.dark-bg {
  background-color: $dark;
  color: whitesmoke;
}

.modal-card-head, .modal-card-title, .box  {
  background-color: $darker !important;
  color: whitesmoke !important;
}

.panel-heading, .panel-block, .filters, .modal-card-foot, .panel-options, .annotations-list-sidebar, .annotations-list-container {
  background-color: $dark !important;
  border-color: $darker !important;
  color: whitesmoke !important;
}

.panel-options td strong, .panel-options > div > div > .label {
  color: whitesmoke;
}

.table, .table thead th, .table td {
  background-color: $dark !important;
  border-color: $darker !important;
  color: whitesmoke !important;
}

a {
  color: $primary;
}

.pagination-link, .vue-slider-process, .vue-slider-dot-tooltip-inner {
  background-color: $primary !important;
  border-color: $primary !important;
}

section ul li {
  color: black;
}

/* For correct display of svg images on IE > 10 */
@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  img[src$=".svg"] {
    width: 100%;
  }
}
</style>
